import React from 'react';
import { Button, Flex, Tag, Typography } from 'antd';
import type { CloudDeck } from '@lingoping/core';
import { DeckProgress } from '../services/deckStatus.js';
import { DeckProgressBar } from './DeckProgressBar.js';
import { tourTarget } from '../options/tourTargets.js';

const { Text } = Typography;

interface Props {
  deck: CloudDeck;
  /** Null until the deck's words have been loaded. */
  progress: DeckProgress | null;
  following: boolean;
  /** A larger card, used for the learner's own decks. */
  large?: boolean;
  onGoThrough: () => void;
  onToggleFollow: () => void;
  onOpen: () => void;
}

/** One deck in the Decks tab: title, size, progress, and what can be done with it. */
export const DeckCard: React.FC<Props> = ({
  deck,
  progress,
  following,
  large,
  onGoThrough,
  onToggleFollow,
  onOpen,
}) => {
  const total = progress?.total ?? deck.word_count;
  return (
    <article
      aria-label={deck.title}
      style={{
        background: '#fff',
        border: following ? '2px solid #1677ff' : '1px solid #e5e7eb',
        borderRadius: 12,
        padding: large ? '18px 20px' : '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: large ? 12 : 10,
        // The 2px border of a followed deck must not move its neighbours.
        margin: following ? 0 : 1,
      }}
    >
      <Flex align="center" gap={8} wrap>
        <Text strong style={{ fontSize: large ? 17 : 16 }}>
          {deck.title}
        </Text>
        {following && (
          <Tag color="blue" style={{ margin: 0 }}>
            Following
          </Tag>
        )}
        <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 13 }}>
          {total} word{total === 1 ? '' : 's'}
        </Text>
      </Flex>
      {deck.description && (
        <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {deck.description}
        </Text>
      )}
      <DeckProgressBar progress={progress} />
      {following && (
        <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
          Echo asks you about this deck's words, in order.
        </Text>
      )}
      <Flex gap={8} wrap>
        <Button
          type={following || large ? 'primary' : 'default'}
          disabled={!!progress && progress.toGo === 0}
          onClick={onGoThrough}
        >
          Go through words
        </Button>
        <Button onClick={onToggleFollow} {...tourTarget('follow')}>
          {following ? 'Stop following' : 'Follow'}
        </Button>
        <Button type="link" onClick={onOpen} aria-label={`Open ${deck.title}`}>
          Open
        </Button>
      </Flex>
    </article>
  );
};
